import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function waitForImages(root: HTMLElement, timeoutMs = 4000): Promise<void> {
  const images = Array.from(root.querySelectorAll("img"));
  if (images.length === 0) return Promise.resolve();

  return Promise.race([
    Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }
            const done = () => resolve();
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
          })
      )
    ).then(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

function triggerPrintFromBlob(blobUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);

    let finished = false;
    const cleanup = () => {
      if (finished) return;
      finished = true;
      try {
        document.body.removeChild(iframe);
      } catch {
        /* already removed */
      }
      URL.revokeObjectURL(blobUrl);
      resolve();
    };

    const doPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (err) {
        reject(err);
        return;
      }
      setTimeout(cleanup, 1000);
    };

    iframe.onload = () => setTimeout(doPrint, 300);
    iframe.src = blobUrl;
    setTimeout(() => {
      if (!finished) doPrint();
    }, 1500);
  });
}

export type PrintElementOptions = {
  contentSelector?: string;
  /** PDF page width in mm (default 210 = A4). Use 80 for thermal token printers. */
  paperWidthMm?: number;
};

export async function printElementToPdf(
  element: HTMLElement,
  options?: PrintElementOptions
): Promise<void> {
  const paperWidthMm = options?.paperWidthMm ?? 210;
  const target =
    (options?.contentSelector
      ? (element.querySelector(options.contentSelector) as HTMLElement | null)
      : null) ?? element;

  if (!target) {
    throw new Error("Print target not found");
  }

  const previousTargetDisplay = target.style.display;
  const previousTargetVisibility = target.style.visibility;
  const previousTargetTransform = target.style.transform;

  target.style.display = "block";
  target.style.visibility = "visible";
  target.style.transform = "none";

  try {
    await waitForImages(target);
    await waitForNextPaint();

    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    if (!canvas.width || !canvas.height) {
      throw new Error("Print preview is empty");
    }

    const imgData = canvas.toDataURL("image/png");
    const imgHeight = (canvas.height / canvas.width) * paperWidthMm;

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: [paperWidthMm, Math.max(imgHeight, paperWidthMm < 80 ? 20 : 40)],
    });

    pdf.addImage(imgData, "PNG", 0, 0, paperWidthMm, imgHeight, "", "FAST");

    const pdfBlob = pdf.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);
    await triggerPrintFromBlob(blobUrl);
  } finally {
    target.style.display = previousTargetDisplay;
    target.style.visibility = previousTargetVisibility;
    target.style.transform = previousTargetTransform;
  }
}
